/* CaseChatThread — case-scoped chat thread + composer.
 *
 * Embeds inside a case workspace tab (planner's "Provider Chat" / doctor's
 * "Lab Chat"). Reuses the same `/cases/{id}/messages` endpoint that powers
 * the standalone /coe/planner/provider-chat and /doctor/lab-chat pages.
 *
 * Props:
 *   caseId   — required, scopes the thread.
 *   role     — "planner" | "doctor"  (controls placeholder copy + "mine" alignment)
 *
 * Behaviour:
 *   - Loads thread on mount, marks unread messages as read.
 *   - Polls every 30s, fast-polls (5s × 6 bursts) for 30s after sending.
 *   - Composer supports text + file attachments (image / pdf / stl / obj / ply).
 *   - Cmd/Ctrl+Enter to send.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    ChatCircleDots, PaperPlaneTilt, Spinner, Paperclip, X, FileText,
    Image as ImageIcon, Check, Checks,
} from "@phosphor-icons/react";

const POLL_MS = 30_000;
const FAST_POLL_MS = 5_000;

function fmtTime(iso) {
    if (!iso) return "";
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return "";
    const now = new Date();
    if (t.toDateString() === now.toDateString()) {
        return t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    }
    if (now.getTime() - t.getTime() < 7 * 86400000) {
        return t.toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false });
    }
    return t.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

function fmtSize(bytes) {
    if (!bytes || Number.isNaN(bytes)) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CaseChatThread({ caseId, role = "planner" }) {
    const [thread, setThread] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState("");
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const pollTimerRef = useRef(null);
    const fileInputRef = useRef(null);

    const reload = useCallback(async (opts = {}) => {
        if (!caseId) return;
        if (!opts.silent) setLoading(true);
        try {
            const { data } = await api.get(`/cases/${caseId}/messages`);
            setThread(data || []);
            if (!opts.skipMarkRead) {
                api.post(`/cases/${caseId}/messages/mark-read`).catch(() => {});
            }
        } catch (err) {
            if (!opts.silent) toast.error(err.response?.data?.detail || "Could not load thread");
        } finally {
            if (!opts.silent) setLoading(false);
        }
    }, [caseId]);

    useEffect(() => { reload(); }, [reload]);

    useEffect(() => {
        if (!caseId) return undefined;
        let cancelled = false;
        pollTimerRef.current = setInterval(() => {
            if (cancelled || document.hidden) return;
            reload({ silent: true, skipMarkRead: true });
        }, POLL_MS);
        return () => {
            cancelled = true;
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        };
    }, [caseId, reload]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [thread.length]);

    const onFilesPicked = useCallback(async (files) => {
        if (!caseId) return;
        const list = Array.from(files || []);
        if (list.length === 0) return;
        for (const file of list) {
            setUploadingCount((n) => n + 1);
            try {
                const form = new FormData();
                form.append("file", file);
                const { data } = await api.post(
                    `/cases/${caseId}/upload?kind=chat_attachment`,
                    form,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );
                setPendingAttachments((arr) => [...arr, {
                    id: data.id,
                    name: data.name,
                    content_type: data.content_type,
                    size: data.size,
                    is_image: (data.content_type || "").startsWith("image/"),
                }]);
            } catch (err) {
                toast.error(err.response?.data?.detail || `Couldn't upload ${file.name}`);
            } finally {
                setUploadingCount((n) => n - 1);
            }
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [caseId]);

    const removePendingAttachment = (id) => {
        setPendingAttachments((arr) => arr.filter((a) => a.id !== id));
    };

    const sendMessage = async () => {
        const text = draft.trim();
        const attachmentIds = pendingAttachments.map((a) => a.id);
        if ((!text && attachmentIds.length === 0) || sending || !caseId) return;
        setSending(true);
        const optimisticId = `opt-${Date.now()}`;
        setThread((m) => [...m, {
            id: optimisticId,
            author_name: "You",
            author_role: role === "doctor" ? "doctor" : "staff",
            author_staff_type: role === "planner" ? "planner" : undefined,
            text,
            attachments: pendingAttachments,
            read_by: [],
            created_at: new Date().toISOString(),
            _pending: true,
        }]);
        setDraft("");
        setPendingAttachments([]);
        try {
            const { data } = await api.post(`/cases/${caseId}/messages`, {
                text, attachment_file_ids: attachmentIds,
            });
            setThread((m) => m.map((x) => (x.id === optimisticId ? data : x)));
            // Burst-poll so we pick up the other side's reply fast.
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            let bursts = 0;
            const fast = setInterval(() => {
                bursts += 1;
                if (bursts > 6) {
                    clearInterval(fast);
                    pollTimerRef.current = setInterval(
                        () => !document.hidden && reload({ silent: true, skipMarkRead: true }),
                        POLL_MS,
                    );
                    return;
                }
                if (!document.hidden) reload({ silent: true, skipMarkRead: true });
            }, FAST_POLL_MS);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't send message");
            setThread((m) => m.filter((x) => x.id !== optimisticId));
        } finally {
            setSending(false);
        }
    };

    const placeholder = role === "doctor"
        ? "Type your message to your Invisique™ planning team…"
        : "Type your message to the clinic…";
    const hint = role === "doctor"
        ? "Press Cmd / Ctrl + Enter to send. Your planning team will see this within seconds."
        : "Press Cmd / Ctrl + Enter to send. Doctors see your name as \"Center of Excellence — Planning\".";

    /* iter101.57 — Header + shell match the Author-the-plan /
       Submission / MFG grammar (gray strip, caps title, italic
       subhead). Copy is role-aware so both the doctor and the
       planner see a header that speaks to them naturally. */
    const headerTitle = role === "doctor" ? "Your Planning Team" : "Provider Chat";
    const headerSubhead = role === "doctor"
        ? "Ask anything about this case — your planning team replies within business hours."
        : "Case-scoped conversation with the doctor. Every message is visible to admin.";

    return (
        <div
            className="rounded-2xl border border-border/70 bg-card overflow-hidden flex flex-col shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]"
            style={{ minHeight: "60vh" }}
            data-testid={`case-chat-thread-${role}`}
        >
            <div className="bg-secondary/30 border-b border-border/70 px-5 sm:px-6 py-3.5">
                <h2 className="text-[11px] font-bold tracking-[0.16em] uppercase text-foreground">
                    {headerTitle}
                </h2>
                <p className="text-[12px] italic text-muted-foreground mt-0.5 max-w-xl">
                    {headerSubhead}
                </p>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-background/30 min-h-[300px]">
                {loading && thread.length === 0 ? (
                    <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                        <Spinner size={16} className="animate-spin mr-2" /> Loading messages…
                    </div>
                ) : thread.length === 0 ? (
                    <div className="text-center text-muted-foreground text-xs py-12">
                        <ChatCircleDots size={22} weight="duotone" className="mx-auto mb-2 text-muted-foreground/60" />
                        No messages yet — {role === "doctor" ? "ask your planning team anything about this case." : "say hello to the clinic."}
                    </div>
                ) : (
                    thread.map((msg) => <MessageBubble key={msg.id} msg={msg} viewerRole={role} />)
                )}
            </div>
            <Composer
                draft={draft}
                setDraft={setDraft}
                onSend={sendMessage}
                sending={sending}
                pendingAttachments={pendingAttachments}
                onFilesPicked={onFilesPicked}
                removePendingAttachment={removePendingAttachment}
                uploadingCount={uploadingCount}
                fileInputRef={fileInputRef}
                placeholder={placeholder}
                hint={hint}
            />
        </div>
    );
}

function MessageBubble({ msg, viewerRole }) {
    // "Mine" depends on viewer:
    //   - viewer is doctor      → mine = author is doctor / clinic_staff
    //   - viewer is planner     → mine = author is staff (COE planner / QA / production)
    const mine = viewerRole === "doctor"
        ? (msg.author_role === "doctor" || msg.author_role === "clinic_staff")
        : (msg.author_role === "staff");

    const meta = (() => {
        if (msg.author_role === "admin") return { label: "Admin", tone: "bg-violet-50 text-violet-700 border-violet-200" };
        if (msg.author_role === "clinic_staff") return { label: `${msg.author_name} · Clinic`, tone: "bg-amber-50 text-amber-800 border-amber-200" };
        if (msg.author_role === "doctor") return { label: msg.author_name, tone: "bg-primary/10 text-primary border-primary/30" };
        // Planner / QA / Production — redact name for doctor's view; show real name for planner view.
        const label = viewerRole === "doctor" ? "Invisique™ Planning Team" : (msg.author_name || "Team");
        return { label, tone: "bg-secondary text-foreground/80 border-border" };
    })();

    const attachments = msg.attachments || [];
    const recipientReads = (msg.read_by || []).filter((r) => r.user_id !== msg.author_id).length;
    const showCheck = mine && !msg._pending;

    return (
        <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`case-chat-msg-${msg.id}`}>
            <div className="max-w-[78%] min-w-0">
                {!mine && (
                    <div className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-semibold border mb-1 ${meta.tone}`}>
                        {meta.label}
                    </div>
                )}
                <div className={`rounded-2xl px-3.5 py-2 text-sm break-words shadow-sm ${
                    mine ? "bg-primary text-white rounded-br-md" : "bg-secondary/70 text-foreground rounded-bl-md"
                } ${msg._pending ? "opacity-60" : ""}`}>
                    {attachments.length > 0 && (
                        <div className={`space-y-1.5 ${msg.text ? "mb-2" : ""}`}>
                            {attachments.map((a) => <AttachmentChip key={a.id} att={a} mine={mine} />)}
                        </div>
                    )}
                    {msg.text && <div className="whitespace-pre-wrap">{msg.text}</div>}
                </div>
                <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? "justify-end text-muted-foreground" : "text-muted-foreground"}`}>
                    <span>{fmtTime(msg.created_at)}{msg._pending && " · sending…"}</span>
                    {showCheck && (
                        recipientReads > 0 ? (
                            <Checks size={12} weight="bold" className="text-primary" title="Read" />
                        ) : (
                            <Check size={12} weight="bold" className="text-muted-foreground/70" title="Sent" />
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

function AttachmentChip({ att, mine }) {
    const isImage = (att.content_type || "").startsWith("image/")
        || /\.(png|jpg|jpeg|gif|webp|heic)$/i.test(att.name || "");
    const [blobUrl, setBlobUrl] = useState(null);

    useEffect(() => {
        if (!isImage || !att.id) return undefined;
        let revoked = false;
        let url = null;
        api.get(`/files/${att.id}`, { responseType: "blob" })
            .then((r) => {
                if (revoked) return;
                url = URL.createObjectURL(r.data);
                setBlobUrl(url);
            })
            .catch(() => {});
        return () => {
            revoked = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [att.id, isImage]);

    const downloadFile = async () => {
        try {
            const r = await api.get(`/files/${att.id}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = att.name || "download";
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        } catch { toast.error("Couldn't download attachment"); }
    };

    if (isImage) {
        return (
            <button
                type="button"
                onClick={downloadFile}
                className="block max-w-[260px] rounded-lg overflow-hidden border border-black/10 bg-black/5 cursor-zoom-in"
                title={att.name}
            >
                {blobUrl ? (
                    <img src={blobUrl} alt={att.name} className="block max-h-[200px] w-auto" />
                ) : (
                    <div className="w-[200px] h-[120px] flex items-center justify-center text-xs text-muted-foreground">
                        <Spinner size={16} className="animate-spin" />
                    </div>
                )}
            </button>
        );
    }
    const cardCls = mine
        ? "bg-white/10 hover:bg-white/20 border-white/20 text-white"
        : "bg-card hover:bg-secondary border-border text-foreground";
    return (
        <button
            type="button"
            onClick={downloadFile}
            className={`flex items-center gap-2 max-w-[260px] px-2.5 py-2 rounded-lg border text-left ${cardCls}`}
        >
            <FileText size={18} weight="duotone" className="shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold truncate">{att.name || "File"}</div>
                <div className={`text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>{fmtSize(att.size)}</div>
            </div>
        </button>
    );
}

function Composer({
    draft, setDraft, onSend, sending,
    pendingAttachments, onFilesPicked, removePendingAttachment,
    uploadingCount, fileInputRef, placeholder, hint,
}) {
    const onKey = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            onSend();
        }
    };
    const canSend = !sending && uploadingCount === 0 && (draft.trim().length > 0 || pendingAttachments.length > 0);
    return (
        <div className="border-t border-border p-3 bg-background/50">
            {(pendingAttachments.length > 0 || uploadingCount > 0) && (
                <div className="mb-2 flex flex-wrap gap-2" data-testid="case-chat-attachments-tray">
                    {pendingAttachments.map((a) => (
                        <div
                            key={a.id}
                            className="relative inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-secondary border border-border text-[11px]"
                            data-testid={`case-chat-tray-${a.id}`}
                        >
                            {a.is_image
                                ? <ImageIcon size={13} weight="duotone" className="text-primary shrink-0" />
                                : <FileText size={13} weight="duotone" className="text-muted-foreground shrink-0" />}
                            <span className="font-medium truncate max-w-[140px]">{a.name}</span>
                            <button
                                type="button"
                                onClick={() => removePendingAttachment(a.id)}
                                className="w-5 h-5 rounded-full hover:bg-rose-100 hover:text-rose-700 flex items-center justify-center transition-colors"
                                title="Remove"
                            >
                                <X size={11} weight="bold" />
                            </button>
                        </div>
                    ))}
                    {uploadingCount > 0 && (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-secondary/70 border border-border text-[11px] text-muted-foreground">
                            <Spinner size={12} className="animate-spin" />
                            Uploading {uploadingCount} file{uploadingCount === 1 ? "" : "s"}…
                        </div>
                    )}
                </div>
            )}
            <div className="flex items-end gap-2">
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => onFilesPicked(e.target.files)}
                    className="hidden"
                    multiple
                    accept="image/*,application/pdf,.stl,.ply,.obj"
                    data-testid="case-chat-file-input"
                />
                <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    size="sm"
                    className="h-10 px-2.5 shrink-0"
                    title="Attach files"
                    data-testid="case-chat-attach-btn"
                >
                    <Paperclip size={15} />
                </Button>
                <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKey}
                    placeholder={placeholder}
                    rows={2}
                    className="resize-none text-sm"
                    data-testid="case-chat-composer"
                />
                <Button
                    type="button"
                    onClick={onSend}
                    disabled={!canSend}
                    size="sm"
                    className="h-10 shrink-0"
                    data-testid="case-chat-send"
                >
                    {sending ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />}
                    <span className="ml-1.5 hidden sm:inline">Send</span>
                </Button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1.5">{hint}</div>
        </div>
    );
}
